import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOnboardingStore } from '../store/onboardingStore'
import { Button } from '../components/Button'
import type { User } from 'firebase/auth'
import { auth, createAccount, signIn, signInWithGoogle, signOutUser, resetPassword, saveUserOnboardingData, sendVerification, isTrustedUser, hasLinkedWebPurchase } from '../lib/firebase'
import { claimWebPurchase } from '../lib/checkSubscription'
import { fireConversionFallback, trackPaidSetupClaimed } from '../lib/analytics'
import { usesAppLook } from '../lib/variant'
import { V2Icon } from '../components/v2'

function GoogleButton({ onClick, disabled, label }: { onClick: () => void; disabled: boolean; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
    >
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.2C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.2C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
      {label}
    </button>
  )
}

export function CreateAccountStep() {
  const { goNext, data } = useOnboardingStore()
  const v2 = usesAppLook()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [resetHints, setResetHints] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [isExistingAccount, setIsExistingAccount] = useState(false)
  const [useNewEmail, setUseNewEmail] = useState(false)
  const [newEmail, setNewEmail] = useState('')

  // Paid on an earlier visit, no account until now (EmailStep found a parked purchase).
  const paidSetup = !!data.paidSetup
  // Set once a paid-setup email account waits for its verification link.
  const [verifyEmail, setVerifyEmail] = useState<string | null>(null)

  const email = useNewEmail ? newEmail : (data.email || '')

  // Fallback: fire conversion if OfferStep tracking failed (3D Secure redirect, etc.)
  useEffect(() => {
    fireConversionFallback()
  }, [])

  const saveDataAndProceed = async (user: User, accountEmail: string, method: 'email' | 'google') => {
    await saveUserOnboardingData(user.uid, {
      ...data,
      email: accountEmail,
    })
    if (!paidSetup) {
      goNext({ authMethod: method, accountEmail })
      return
    }
    await finishPaidSetup(user, accountEmail, method)
  }

  // Attach the waiting purchase. The server attaches it only to an account that
  // owns the email: Google proves it at sign-in, a password account by clicking
  // the verification link (functions/src/paddlePending.ts).
  const finishPaidSetup = async (user: User, accountEmail: string, method: 'email' | 'google') => {
    const status = await claimWebPurchase()
    if (status === 'pending_unverified') {
      try {
        await sendVerification(user)
      } catch (err) {
        console.error('Verification email error:', err)
      }
      setVerifyEmail(accountEmail)
      return
    }
    // 'none' can also mean the server's profile trigger linked it a moment earlier.
    const claimed = status === 'claimed' || (await hasLinkedWebPurchase(user.uid))
    if (claimed) trackPaidSetupClaimed(method)
    goNext({ authMethod: method, accountEmail, webPurchaseClaimed: claimed })
  }

  const handleVerified = async (accountEmail: string) => {
    const user = auth.currentUser
    if (!user) {
      setVerifyEmail(null)
      return
    }
    setError('')
    setSuccessMessage('')
    setLoading(true)
    try {
      await user.reload()
      const status = await claimWebPurchase()
      if (status === 'pending_unverified') {
        setError("That email isn't verified yet. Open the link in the email first.")
        return
      }
      const claimed = status === 'claimed' || (await hasLinkedWebPurchase(user.uid))
      // A reload can land here with a Google session, not only a verified password account.
      const method = user.providerData.some((p) => p.providerId === 'google.com') ? 'google' : 'email'
      if (claimed) trackPaidSetupClaimed(method)
      goNext({ authMethod: method, accountEmail, webPurchaseClaimed: claimed })
    } catch (err) {
      console.error('Verification check error:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    const user = auth.currentUser
    if (!user) return
    setError('')
    setSuccessMessage('')
    setLoading(true)
    try {
      await sendVerification(user)
      setSuccessMessage('Sent again. Check your inbox and spam folder.')
    } catch {
      setError("Couldn't send the email. Wait a minute and try again.")
    } finally {
      setLoading(false)
    }
  }

  // Paid setup, back on this page with the account already made (the verification
  // link's Continue button, or a reload): pick up where the buyer left off.
  useEffect(() => {
    if (!paidSetup) return
    let cancelled = false
    void (async () => {
      await auth.authStateReady()
      const user = auth.currentUser
      if (cancelled || !user?.email || user.email.toLowerCase() !== (data.email || '').toLowerCase()) return
      const cameBackVerified = new URLSearchParams(window.location.search).get('verified') === '1'
      if (cameBackVerified) window.history.replaceState(window.history.state, '', window.location.pathname)
      setVerifyEmail(user.email)
      if (cameBackVerified || isTrustedUser(user)) void handleVerified(user.email)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on mount
  }, [])

  const handleCreateAccount = async () => {
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      const user = await createAccount(email, password)
      await saveDataAndProceed(user, email, 'email')
    } catch (err: unknown) {
      console.error('Account creation error:', err)
      if (err instanceof Error && err.message.includes('email-already-in-use')) {
        setIsExistingAccount(true)
        setPassword('')
        setConfirmPassword('')
        setError('')
      } else {
        setError('Failed to create account. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSignIn = async () => {
    setError('')
    setSuccessMessage('')
    setResetHints([])

    if (!password) {
      setError('Please enter your password')
      return
    }

    setLoading(true)

    try {
      const user = await signIn(data.email || '', password)
      await saveDataAndProceed(user, data.email || '', 'email')
    } catch (err: unknown) {
      console.error('Sign in error:', err)
      if (err instanceof Error && (err.message.includes('wrong-password') || err.message.includes('invalid-credential'))) {
        setError('Incorrect password. Please try again.')
      } else {
        setError('Failed to sign in. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // App accounts created with "Sign in with Google" have NO password, so the
  // password form can never succeed for them — this button is their only path
  // to linking the purchase they just made.
  const handleGoogleSignIn = async () => {
    setError('')
    setSuccessMessage('')
    setResetHints([])
    setLoading(true)

    try {
      const user = await signInWithGoogle(data.email || undefined)
      const googleEmail = user.email || ''
      // The purchase waits under the email typed in the funnel; a Google account
      // with another address can't claim it.
      if (paidSetup && googleEmail.toLowerCase() !== (data.email || '').toLowerCase()) {
        await signOutUser()
        setError(`That Google account is ${googleEmail || 'a different email'}. Your purchase is under ${data.email}. Choose that Google account, or use a password instead.`)
        return
      }
      await saveDataAndProceed(user, googleEmail || data.email || '', 'google')
    } catch (err: unknown) {
      console.error('Google sign in error:', err)
      if (err instanceof Error && err.message.includes('popup-closed-by-user')) {
        // User dismissed the popup — not an error worth showing
      } else if (err instanceof Error && err.message.includes('popup-blocked')) {
        setError('Your browser blocked the sign-in window. Please allow popups and try again.')
      } else {
        setError('Google sign-in failed. Please try again or use your password.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    setError('')
    setSuccessMessage('')
    setResetHints([])
    setLoading(true)

    try {
      await resetPassword(data.email || '')
      // Email-enumeration protection is ON: this resolves for an address with no
      // password account and sends nothing, so never promise an email. The hints
      // are static (4.38 U1) — sign-in methods are never looked up.
      setSuccessMessage("If this email is eligible for a password reset, we'll send a link. Check your spam folder too.")
      setResetHints([
        'Usually sign in with Google? You may not have a password to reset — use the Google button above.',
        'Signed up with Apple? Open the KidneyPal app and continue with Apple.',
      ])
    } catch {
      setError('Failed to send reset email. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleUseNewEmail = () => {
    setIsExistingAccount(false)
    setUseNewEmail(true)
    setPassword('')
    setConfirmPassword('')
    setError('')
    setSuccessMessage('')
    setResetHints([])
  }

  // Paid setup with an email account: verify the address, then the purchase attaches
  if (verifyEmail) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <div className="text-center mb-8">
              {v2 ? <V2Icon name="mascot-waving" size={80} className="block mx-auto mb-4" /> : <div className="text-5xl mb-4">📧</div>}
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h1>
              <p className="text-gray-600">
                We sent a link to <strong className="text-gray-900 break-words">{verifyEmail}</strong>. Open it to confirm this email is yours. Then come back here and tap &ldquo;I&rsquo;ve verified&rdquo;.
              </p>
              <p className="text-sm text-gray-500 mt-2">Your Pro purchase is added to your account after this step. Check your spam folder too.</p>
            </div>

            <div className="space-y-4">
              {error && <p className="text-red-500 text-sm text-center">{error}</p>}
              {successMessage && <p className="text-green-600 text-sm text-center">{successMessage}</p>}

              <Button onClick={() => void handleVerified(verifyEmail)} disabled={loading}>
                {loading ? 'Checking…' : 'I\u2019ve verified'}
              </Button>

              <div className="text-center">
                <button type="button" onClick={handleResendVerification} disabled={loading} className="text-sm text-primary hover:underline">
                  Send the email again
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  // Existing account - sign in
  if (isExistingAccount) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <div className="text-center mb-8">
              {v2 ? <V2Icon name="mascot-waving" size={80} className="block mx-auto mb-4" /> : <div className="text-5xl mb-4">👋</div>}
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome back!</h1>
              <p className="text-gray-600">You already have an account. Sign in to link your new plan.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={data.email || ''} disabled className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-500" />
              </div>

              <GoogleButton onClick={handleGoogleSignIn} disabled={loading} label="Sign in with Google" />
              <p className="text-xs text-gray-500 text-center -mt-1">
                Used &ldquo;Sign in with Google&rdquo; in the KidneyPal app? Tap the button above — your account has no password.
              </p>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">or use your password</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <button type="button" onClick={handleForgotPassword} disabled={loading} className="text-sm text-primary hover:underline">Forgot password?</button>
                </div>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>

              {error && <p className="text-red-500 text-sm text-center">{error}</p>}
              {successMessage && <p className="text-green-600 text-sm text-center">{successMessage}</p>}
              {resetHints.map((hint) => (
                <p key={hint} className="text-xs text-gray-500 text-center">{hint}</p>
              ))}

              <Button onClick={handleSignIn} disabled={loading || !password}>
                {loading ? 'Signing In...' : 'Sign In & Continue'}
              </Button>

              <div className="text-center">
                <p className="text-xs text-gray-500 mb-2">Your new plan will be linked to your existing account</p>
                {/* Paid setup: the purchase waits under this email, so no switching here. */}
                {!paidSetup && (
                  <button type="button" onClick={handleUseNewEmail} className="text-sm text-primary hover:underline">Use a different email instead</button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  // New account
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            {v2 ? <V2Icon name="mascot-clapping" size={80} className="block mx-auto mb-4" /> : <div className="text-5xl mb-4">🎉</div>}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {paidSetup ? 'Finish setting up your account' : data.paddleTransactionId ? 'Payment Successful!' : 'Create Your Account'}
            </h1>
            <p className="text-gray-600">
              {paidSetup
                ? 'Your KidneyPal Pro purchase is waiting for this email. Set up your app login below to start using it.'
                : data.paddleTransactionId
                  ? 'One last step — set up your app login below'
                  : 'Sign up to get started with KidneyPal'}
            </p>
          </div>

          {(data.paddleTransactionId || paidSetup) && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3 mb-2 flex items-start gap-3">
              <span className="text-lg mt-0.5">📱</span>
              <p className="text-sm text-gray-700">
                This email and password is what you'll use to <strong className="text-gray-900">sign in to the KidneyPal app</strong> on your phone.
              </p>
            </div>
          )}

          <div className="space-y-4">
            {paidSetup && (
              <>
                <GoogleButton onClick={handleGoogleSignIn} disabled={loading} label="Continue with Google" />
                <p className="text-xs text-gray-500 text-center -mt-1">
                  Is {data.email} a Google account? Use the button above — no password needed.
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400">or create a password</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              {useNewEmail ? (
                <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Enter your email" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
              ) : (
                <input type="email" value={data.email || ''} disabled className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-500" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Create Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm your password" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>

            {error && <p className="text-red-500 text-sm text-center">{error}</p>}

            <Button onClick={handleCreateAccount} disabled={loading || !password || !confirmPassword || (useNewEmail && !newEmail)}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <p className="text-xs text-gray-400 text-center">
              Remember these credentials — you'll need them to log in to the app
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
